import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, Col, Empty, Row, Spin, Typography, Tag } from 'antd';
import ToolCard from '../components/ToolCard';
import { categoriesAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import { usePageSeo } from '../utils/usePageSeo';
import { getCategoryLabel } from '../utils/categoryLocale';

const { Title, Paragraph, Text } = Typography;

const CategoryPage: React.FC = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const zh = language === 'zh-CN';
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    categoriesAPI
      .getBySlug(slug)
      .then((res) => {
        if (cancelled) return;
        setCategory(res.data?.category || null);
      })
      .catch(() => {
        if (!cancelled) setCategory(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const label = category ? getCategoryLabel(category, language) : slug;
  const intro = category
    ? zh
      ? category.intro_zh || category.description || ''
      : category.intro_en || category.description || ''
    : '';
  const title = category
    ? zh
      ? category.seo_title_zh || `最佳 ${label} AI 工具`
      : category.seo_title_en || `Best ${label} AI Tools`
    : label;

  usePageSeo({
    title,
    description: intro || (zh ? `浏览 ${label} 分类下的 AI 工具` : `Browse AI tools in ${label}`),
    path: `/category/${slug}`,
  });

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!category) {
    return (
      <div style={{ padding: 48 }}>
        <Empty description={zh ? '分类不存在' : 'Category not found'} />
      </div>
    );
  }

  const tools = category.tools || [];
  const children = category.children || [];
  const siblings = category.siblings || [];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 48px' }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <a onClick={() => navigate('/')}>{zh ? '首页' : 'Home'}</a> },
          { title: <a onClick={() => navigate('/tools')}>{zh ? '工具库' : 'Tools'}</a> },
          ...(category.parent
            ? [{ title: <a onClick={() => navigate(`/category/${category.parent.slug}`)}>{getCategoryLabel(category.parent, language)}</a> }]
            : []),
          { title: label },
        ]}
      />
      <Title level={2} style={{ marginBottom: 8 }}>{title}</Title>
      {intro ? <Paragraph type="secondary">{intro}</Paragraph> : null}
      <Text type="secondary">{zh ? `${tools.length} 个工具` : `${tools.length} tools`}</Text>

      {children.length > 0 && (
        <div style={{ marginTop: 20, marginBottom: 8 }}>
          <Text strong>{zh ? '子分类' : 'Subcategories'}</Text>
          <div style={{ marginTop: 8 }}>
            {children.map((c: any) => (
              <Tag
                key={c.slug}
                style={{ cursor: 'pointer', marginBottom: 8 }}
                onClick={() => navigate(`/category/${c.slug}`)}
              >
                {getCategoryLabel(c, language)}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {tools.length === 0 ? (
        <Empty style={{ marginTop: 48 }} description={zh ? '该分类暂无工具' : 'No tools in this category'} />
      ) : (
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          {tools.map((tool: any) => (
            <Col key={tool.id || tool.slug} xs={24} sm={12} md={8} lg={6}>
              <ToolCard tool={tool} />
            </Col>
          ))}
        </Row>
      )}

      {siblings.length > 0 && (
        <div style={{ marginTop: 40 }}>
          <Title level={4}>{zh ? '相关分类' : 'Related categories'}</Title>
          <div>
            {siblings.map((s: any) => (
              <Tag key={s.slug} style={{ cursor: 'pointer', marginBottom: 8 }} onClick={() => navigate(`/category/${s.slug}`)}>
                {getCategoryLabel(s, language)}
              </Tag>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryPage;
